import {
  feriadoEm,
  formatRRule,
  parseRRule,
  WEEKDAY_CODES,
  type Appointment,
  type Reminder,
} from '@secretario/shared';

/**
 * Regras de tela da Agenda: o que cai em cada dia, o mês em grade, o tempo
 * livre, os rótulos de data por extenso e a repetição em linguagem de gente.
 * Tudo puro, na hora local do aparelho.
 */

export type AgendaEntry = {
  key: string;
  kind: 'appointment' | 'reminder';
  title: string;
  start: Date;
  /** Fim do compromisso (num dia inteiro, a meia-noite depois do último dia). */
  end: Date | null;
  allDay: boolean;
  repeats: boolean;
  appointment?: Appointment;
  reminder?: Reminder;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

export function dayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
export function dateOfKey(key: string): Date {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}
export function addDays(key: string, days: number): string {
  const date = dateOfKey(key);
  date.setDate(date.getDate() + days);
  return dayKey(date);
}

/** Compromissos e lembretes numa lista só, sem o que foi cancelado ou apagado. */
export function entriesOf(appointments: Appointment[], reminders: Reminder[]): AgendaEntry[] {
  return [
    ...appointments
      .filter((item) => item.status !== 'cancelled' && !item.deletedAt)
      .map((item) => ({
        key: `a-${item.id}`,
        kind: 'appointment' as const,
        title: item.title,
        start: new Date(item.startsAt),
        end: item.endsAt ? new Date(item.endsAt) : null,
        allDay: item.allDay === true,
        repeats: !!item.seriesId || !!item.rrule,
        appointment: item,
      })),
    ...reminders
      .filter((item) => item.status !== 'cancelled' && !item.deletedAt)
      .map((item) => ({
        key: `r-${item.id}`,
        kind: 'reminder' as const,
        title: item.message,
        start: new Date(item.remindAt),
        end: null,
        allDay: false,
        repeats: false,
        reminder: item,
      })),
  ];
}

/** Os dias que o item ocupa (um dia inteiro de vários dias aparece em todos). */
function daysOf(entry: AgendaEntry): string[] {
  const first = dayKey(entry.start);
  if (!entry.allDay || !entry.end) return [first];
  const days = Math.max(1, Math.round((entry.end.getTime() - entry.start.getTime()) / DAY_MS));
  return Array.from({ length: Math.min(days, 60) }, (_, index) => addDays(first, index));
}

/** O que há num dia: primeiro o que dura o dia inteiro, depois por hora. */
export function entriesOn(entries: AgendaEntry[], key: string): AgendaEntry[] {
  return entries
    .filter((entry) => daysOf(entry).includes(key))
    .sort(
      (a, b) =>
        Number(b.allDay) - Number(a.allDay) ||
        a.start.getTime() - b.start.getTime() ||
        a.title.localeCompare(b.title),
    );
}

/** Quantas coisas há em cada dia (para os pontos do mês e da semana). */
export function countByDay(entries: AgendaEntry[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const entry of entries) {
    for (const key of daysOf(entry)) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

export type MonthCell = { key: string; day: number; inMonth: boolean };

/** O mês em semanas de domingo a sábado, com os dias vizinhos para fechar a grade. */
export function monthGrid(year: number, month: number): MonthCell[][] {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  const weeks: MonthCell[][] = [];
  for (let week = 0; week < 6; week += 1) {
    const cells: MonthCell[] = [];
    for (let day = 0; day < 7; day += 1) {
      const date = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate() + week * 7 + day,
      );
      cells.push({ key: dayKey(date), day: date.getDate(), inMonth: date.getMonth() === month });
    }
    if (week > 3 && cells.every((cell) => !cell.inMonth)) break;
    weeks.push(cells);
  }
  return weeks;
}

/** A semana (domingo a sábado) que contém o dia. */
export function weekOf(key: string): string[] {
  const date = dateOfKey(key);
  const sunday = addDays(key, -date.getDay());
  return Array.from({ length: 7 }, (_, index) => addDays(sunday, index));
}

const clock = (date: Date) =>
  date.getMinutes() === 0
    ? `${date.getHours()}h`
    : `${date.getHours()}h${String(date.getMinutes()).padStart(2, '0')}`;
const minuteLabel = (minutes: number) =>
  minutes % 60 === 0
    ? `${minutes / 60}h`
    : `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, '0')}`;

/** "15h", "15h às 16h30" ou "Dia inteiro". */
export function whenLabel(entry: AgendaEntry): string {
  if (entry.allDay) return 'Dia inteiro';
  if (entry.end && dayKey(entry.end) === dayKey(entry.start) && entry.end > entry.start) {
    return `${clock(entry.start)} às ${clock(entry.end)}`;
  }
  return clock(entry.start);
}

/** Data sem ambiguidade: "qui, 09/10" (com o ano quando não é o atual). */
export function dateLabel(key: string, now = new Date()): string {
  const date = dateOfKey(key);
  const base = `${WEEKDAYS_SHORT[date.getDay()]}, ${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
  return date.getFullYear() === now.getFullYear() ? base : `${base}/${date.getFullYear()}`;
}

/** "qui, 09/10 às 15h" ou "qui, 09/10, dia inteiro": o que a confirmação mostra. */
export function absoluteLabel(start: Date, allDay: boolean, now = new Date()): string {
  const day = dateLabel(dayKey(start), now);
  return allDay ? `${day}, dia inteiro` : `${day} às ${clock(start)}`;
}

/** "Hoje", "Amanhã", "Ontem" ou o dia da semana por extenso com a data. */
export function dayTitle(key: string, now = new Date()): string {
  const days = Math.round((dateOfKey(key).getTime() - dateOfKey(dayKey(now)).getTime()) / DAY_MS);
  if (days === 0) return 'Hoje';
  if (days === 1) return 'Amanhã';
  if (days === -1) return 'Ontem';
  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(dateOfKey(key));
}

/**
 * Janelas livres do dia entre `from` e `to` horas (só compromissos com hora
 * ocupam; lembrete não ocupa). Devolve as de uma hora ou mais, em minutos.
 */
export function freeWindows(
  entries: AgendaEntry[],
  key: string,
  from = 8,
  to = 20,
): [number, number][] {
  const dayStart = dateOfKey(key).getTime();
  const busy = entries
    .filter((entry) => entry.kind === 'appointment' && !entry.allDay && dayKey(entry.start) === key)
    .map((entry) => {
      const begin = Math.round((entry.start.getTime() - dayStart) / 60_000);
      const finish = entry.end ? Math.round((entry.end.getTime() - dayStart) / 60_000) : begin + 60;
      return [begin, Math.max(finish, begin + 15)] as [number, number];
    })
    .sort((a, b) => a[0] - b[0]);
  const windows: [number, number][] = [];
  let cursor = from * 60;
  for (const [begin, finish] of busy) {
    if (begin - cursor >= 60) windows.push([cursor, Math.min(begin, to * 60)]);
    cursor = Math.max(cursor, finish);
  }
  if (to * 60 - cursor >= 60) windows.push([cursor, to * 60]);
  return windows.filter(([begin, finish]) => finish - begin >= 60);
}

/** "Livre das 8h às 14h e das 16h às 20h". Vazio quando não há janela. */
export function freeLabel(windows: [number, number][]): string {
  if (windows.length === 0) return '';
  const parts = windows
    .slice(0, 3)
    .map(([begin, finish]) => `das ${minuteLabel(begin)} às ${minuteLabel(finish)}`);
  const last = parts.pop();
  return `Livre ${parts.length ? `${parts.join(', ')} e ${last}` : last}`;
}

/** Feriado nacional do dia, se houver. */
export function holidayName(key: string): string | null {
  try {
    return feriadoEm(key)?.nome ?? null;
  } catch {
    return null;
  }
}

// ── Repetição ──

export type RepeatChoice = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
export const REPEAT_CHOICES: { id: RepeatChoice; label: string }[] = [
  { id: 'none', label: 'Não repete' },
  { id: 'daily', label: 'Todo dia' },
  { id: 'weekly', label: 'Toda semana' },
  { id: 'monthly', label: 'Todo mês' },
  { id: 'yearly', label: 'Todo ano' },
];
const FREQ: Record<Exclude<RepeatChoice, 'none'>, 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'> = {
  daily: 'DAILY',
  weekly: 'WEEKLY',
  monthly: 'MONTHLY',
  yearly: 'YEARLY',
};

/** A regra de repetição a partir da escolha; `weekdays` (0 = domingo) só vale na semanal. */
export function buildRRule(choice: RepeatChoice, weekdays: number[] = []): string | null {
  if (choice === 'none') return null;
  const days = [...new Set(weekdays)].filter((day) => day >= 0 && day <= 6).sort();
  return formatRRule({
    freq: FREQ[choice],
    interval: 1,
    ...(choice === 'weekly' && days.length > 0
      ? { byDay: days.map((day) => WEEKDAY_CODES[day]!) }
      : {}),
  });
}

/** A escolha e os dias a partir de uma regra guardada (regra fora do simples vira "custom"). */
export function readRRule(text: string | null | undefined): {
  choice: RepeatChoice | 'custom';
  weekdays: number[];
} {
  if (!text) return { choice: 'none', weekdays: [] };
  try {
    const rule = parseRRule(text);
    const simple = rule.interval === 1 && !rule.count && !rule.until && !rule.byMonthDay?.length;
    const weekdays = (rule.byDay ?? []).map((code) => WEEKDAY_CODES.indexOf(code));
    const choice = (Object.keys(FREQ) as Exclude<RepeatChoice, 'none'>[]).find(
      (key) => FREQ[key] === rule.freq,
    );
    if (!simple || !choice || (choice !== 'weekly' && weekdays.length > 0)) {
      return { choice: 'custom', weekdays };
    }
    return { choice, weekdays };
  } catch {
    return { choice: 'custom', weekdays: [] };
  }
}

/** "Toda semana, seg e qua", "Todo mês" ou "Repete" quando a regra é fora do simples. */
export function repeatLabel(text: string | null | undefined): string {
  const { choice, weekdays } = readRRule(text);
  if (choice === 'none') return '';
  if (choice === 'custom') return 'Repete';
  const base = REPEAT_CHOICES.find((item) => item.id === choice)?.label ?? 'Repete';
  if (choice !== 'weekly' || weekdays.length === 0) return base;
  const names = weekdays.map((day) => WEEKDAYS_SHORT[day] ?? '');
  const last = names.pop();
  return `${base}, ${names.length ? `${names.join(', ')} e ${last}` : last}`;
}

/** Antecedências de alerta oferecidas na tela, em minutos. */
export const ALERT_CHOICES: { minutes: number; label: string }[] = [
  { minutes: 0, label: 'Na hora' },
  { minutes: 10, label: '10 min antes' },
  { minutes: 60, label: '1 hora antes' },
  { minutes: 1440, label: '1 dia antes' },
];
